import React, { useRef, useState } from 'react';

const BODY_CHART_URL =
  'https://hpktowjymvthhcvtqfhn.supabase.co/storage/v1/object/public/assets/body.png';

const STAGE_COLORS = {
  'Stage I': '#F59E0B',
  'Stage II': '#F97316',
  'Stage III': '#EF4444',
  'Stage IV': '#7C3AED',
  'Unstageable': '#1F2937',
  'DTI': '#1F2937',
  'Surgical wound': '#3B82F6',
  'Skin tear': '#10B981',
  'Excoriation': '#EC4899',
  'default': '#6B7280'
};

const getColor = (stage) => {
  for (const key of Object.keys(STAGE_COLORS)) {
    if (stage && stage.startsWith(key)) return STAGE_COLORS[key];
  }
  return STAGE_COLORS.default;
};

const AnatomicalDiagram = ({ wounds = [], markers = [], onChange }) => {
  const [placingWound, setPlacingWound] = useState(null);
  const [currentMarkers, setCurrentMarkers] = useState(markers);
  const [imageFailed, setImageFailed] = useState(false);
  const imgRef = useRef(null);

  const handleImageClick = (e) => {
    if (!placingWound) return;
    const rect = imgRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    const color = getColor(placingWound.stage);
    const existing = currentMarkers.findIndex(
      m => m.woundNumber === placingWound.woundNumber
    );
    let updated;
    if (existing >= 0) {
      updated = [...currentMarkers];
      updated[existing] = { ...placingWound, xPct, yPct, color };
    } else {
      updated = [...currentMarkers,
        { ...placingWound, xPct, yPct, color }];
    }
    setCurrentMarkers(updated);
    onChange?.(updated);
    setPlacingWound(null);
  };

  const isPlaced = (woundNumber) =>
    currentMarkers.some(m => m.woundNumber === woundNumber);

  return (
    <div>
      <div style={{ position: 'relative', display: 'inline-block',
        width: '100%', maxWidth: 640 }}>
        {!imageFailed && (
          <img
            ref={imgRef}
            src={BODY_CHART_URL}
            alt="Body diagram — click to place wound markers"
            style={{
              width: '100%',
              cursor: placingWound ? 'crosshair' : 'default',
              border: placingWound ? '2px dashed #6B2D9E' : '1px solid #e5e7eb',
              borderRadius: 8,
              display: 'block'
            }}
            onClick={handleImageClick}
            onError={(e) => {
              e.target.style.display = 'none';
              setImageFailed(true);
            }}
          />
        )}
        {imageFailed && (
          <div style={{
            padding: 24,
            background: '#f3f4f6',
            border: '1px dashed #9ca3af',
            borderRadius: 8,
            textAlign: 'center',
            color: '#4b5563',
            fontSize: 13
          }}>
            Body chart unavailable — mark wound location in the text field below
          </div>
        )}
        {currentMarkers.map((m, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${m.xPct}%`,
            top: `${m.yPct}%`,
            transform: 'translate(-50%, -50%)',
            width: 28, height: 28,
            borderRadius: '50%',
            background: m.color,
            border: '2px solid white',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center',
            color: 'white', fontSize: 11,
            fontWeight: 'bold',
            pointerEvents: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
            zIndex: 10
          }}>
            {m.woundNumber}
          </div>
        ))}
      </div>

      {/* Wound placement controls */}
      {wounds.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontWeight: 600, fontSize: 13, color: '#374151',
            marginBottom: 8 }}>
            {placingWound
              ? `👆 Click on the body diagram above to place Wound #${placingWound.woundNumber}`
              : 'Place wound markers on the diagram:'}
          </p>
          {wounds.map((w) => (
            <div key={w.woundNumber} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 6, padding: '6px 10px',
              background: '#f9fafb', borderRadius: 6 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: getColor(w.stage),
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: 'white',
                fontSize: 11, fontWeight: 'bold', flexShrink: 0
              }}>{w.woundNumber}</div>
              <span style={{ fontSize: 13, flex: 1 }}>
                {w.location || `Wound ${w.woundNumber}`} — {w.stage || 'No stage selected'}
              </span>
              <button type="button"
                onClick={() => setPlacingWound(
                  placingWound?.woundNumber === w.woundNumber ? null : {
                    woundNumber: w.woundNumber,
                    label: w.location,
                    stage: w.stage
                  }
                )}
                style={{
                  background: placingWound?.woundNumber === w.woundNumber
                    ? '#EF4444' : isPlaced(w.woundNumber)
                    ? '#10B981' : '#6B2D9E',
                  color: 'white', border: 'none', borderRadius: 6,
                  padding: '4px 10px', fontSize: 12, cursor: 'pointer'
                }}>
                {placingWound?.woundNumber === w.woundNumber
                  ? '✕ Cancel'
                  : isPlaced(w.woundNumber)
                  ? '✓ Placed — Move'
                  : 'Place marker'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnatomicalDiagram;
